import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePreviewDraftStore } from '../../../store/preview-draft';
import { usePreviewService } from '../../../contexts/PreviewServiceContext';
import { decodeReportParam } from '../../../system/preview';
import { useTenantPath } from '../../../hooks/useTenantPath';

/** Encapsulates all OTP flow logic: decode, send, verify, navigate */
export const useOtpFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getPath } = useTenantPath();
  const { sendOtp, verifyOtp } = usePreviewService();

  const {
    reportPayload,
    otpStage,
    selectedDelivery,
    otpError,
    otpErrorCode,
    attemptsRemaining,
    previewToken,
    isLoading,
    setReportPayload,
    setSelectedDelivery,
    setOtpStage,
    sendOtp: storeSendOtp,
    verifyOtp: storeVerifyOtp,
  } = usePreviewDraftStore();

  const reportParam = searchParams.get('report');

  // Decode report param on mount
  useEffect(() => {
    if (reportParam && !reportPayload) {
      const decoded = decodeReportParam(reportParam);
      if (decoded) {
        setReportPayload(decoded);
      }
    }
  }, [reportParam, reportPayload, setReportPayload]);

  // Navigate to preview on success
  useEffect(() => {
    if (otpStage === 'success' && previewToken && reportPayload) {
      const previewPath = getPath(
        `/preview/${reportPayload.listingKey}?token=${encodeURIComponent(previewToken)}`,
      );
      navigate(previewPath, { replace: true });
    }
  }, [otpStage, previewToken, reportPayload, navigate, getPath]);

  const handleSendOtp = () => {
    if (!reportPayload) return;
    storeSendOtp(sendOtp, {
      listingKey: reportPayload.listingKey,
      owner: reportPayload.ownerNumber,
      destinationType: selectedDelivery === 'sms' ? 'phone' : 'email',
      email: reportPayload.email,
      phone: reportPayload.phone,
    });
  };

  const handleVerifyOtp = (code: string) => {
    if (!reportPayload) return;
    const destination =
      selectedDelivery === 'sms'
        ? reportPayload.phone || reportPayload.email
        : reportPayload.email;
    storeVerifyOtp(verifyOtp, {
      listingKey: reportPayload.listingKey,
      owner: reportPayload.ownerNumber,
      email: reportPayload.email,
      destination,
      token: code,
    });
  };

  const handleRetry = () => {
    setOtpStage('delivery_select');
  };

  return {
    reportPayload,
    reportParam,
    otpStage,
    selectedDelivery,
    otpError,
    otpErrorCode,
    attemptsRemaining,
    isLoading,
    setSelectedDelivery,
    handleSendOtp,
    handleVerifyOtp,
    handleRetry,
  };
};
